<template>
  <div class="widget-resource-page">
    <!-- SECTION 1: HERO -->
    <section class="hero">
      <div class="container">
        <h1>Cricket Widget</h1>
        <p>Go live with real-time cricket scores on your website in minutes.
          One simple embed widget, zero integration hassle.</p>
        <div class="hero-cta">
          <TextButton
            :buttonType="'rs-button yellow-cta'"
            :link="{ name: 'Buy Now', url: '/v5/user/project/review' }"
          />
          <TextButton
            :buttonType="'rs-button white-cta'"
            :link="{ name: 'Talk to our team', url: 'https://cal.com/ashiey-joe-pfn0im', target: '_blank' }"
          />
        </div>
      </div>
    </section>

    <!-- SECTION 2: WHAT IS CRICKET WIDGET -->
    <section class="what-is">
      <div class="container">
        <h2 class="fade-in">What is Cricket Widget?</h2>
        <div class="what-is-content">
          <p class="what-is-intro fade-in">A widget is a small, ready-to-use component that displays specific
            information (like live cricket scores) on a website or app. It lets you easily embed dynamic
            features from CricketAPI without building everything from scratch.</p>

          <div class="widget-cards">
            <div class="widget-card fade-in">
              <h3>Matches Widget</h3>
              <p>Featured match cards that expand into complete scorecards with live ball-by-ball commentary,
                detailed statistics, partnerships, team lineups, and performance graphs.
                Everything updates in real time.</p>
            </div>
            <div class="widget-card fade-in">
              <h3>Tournament Widget</h3>
              <p>Complete tournament view with fixture schedules, team rosters, squad details,
                points tables, standings, and tournament-wide statistics.
                Perfect for leagues and series coverage.</p>
            </div>
          </div>

          <p class="what-is-footer fade-in">Purchase a license, generate your configuration keys, hand them to
            your development team, and the widgets integrate into your site.
            Zero ongoing maintenance required.</p>
        </div>
      </div>
    </section>

    <!-- SECTION 3: TRANSITION -->
    <section class="transition">
      <div class="container">
        <h2 class="fade-in">How the widgets work</h2>
        <p class="fade-in">Both widgets are designed to give cricket fans everything they need without leaving
          your site. Here's what makes them powerful:</p>
      </div>
    </section>

    <!-- SECTION 4: FEATURES -->
    <section class="features">
      <div class="container">
        <!-- Feature 1: Matches Widget -->
        <div class="feature">
          <div class="feature-text slide-in-left">
            <div class="feature-label">Matches Widget</div>
            <h3>Live scores and detailed match coverage</h3>
            <p>Featured match cards that expand into complete scorecard views.
              Users get everything they need without leaving your site.</p>
            <div class="feature-highlights">
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">⚡</div>
                <div class="feature-highlight-text">Live ball-by-ball commentary and scores</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">📊</div>
                <div class="feature-highlight-text">Detailed scorecards with every run and wicket</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">👥</div>
                <div class="feature-highlight-text">Team lineups and player details</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">📈</div>
                <div class="feature-highlight-text">Performance graphs and statistics</div>
              </div>
            </div>
          </div>
          <div class="feature-image slide-in-right">
            <img
              :src="imgScheduled"
              alt="Matches Widget"
            >
          </div>
        </div>

        <!-- Feature 2: Tournament Widget -->
        <div class="feature reverse">
          <div class="feature-text slide-in-right">
            <div class="feature-label">Tournament Widget</div>
            <h3>Complete tournament coverage</h3>
            <p>A dedicated interface for leagues and series. Users browse tournaments
              and access all the context they need.</p>
            <div class="feature-highlights">
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">📅</div>
                <div class="feature-highlight-text">Full fixture schedules with dates and venues</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">🎯</div>
                <div class="feature-highlight-text">Points tables and current standings</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">👥</div>
                <div class="feature-highlight-text">Team rosters and squad details</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">📊</div>
                <div class="feature-highlight-text">Tournament-wide statistics</div>
              </div>
            </div>
          </div>
          <div class="feature-image slide-in-left">
            <img
              :src="imgTournament"
              alt="Tournament Widget"
            >
          </div>
        </div>

        <!-- Feature 3: Real-time Updates -->
        <div class="feature">
          <div class="feature-text slide-in-left">
            <div class="feature-label">Real-time Technology</div>
            <h3>Change persistent server connection to Handles multiple concurrent request </h3>
            <p>Both widgets use streaming technology to stay current during live matches.
              Scores, wickets, and commentary update instantly as the game progresses.</p>
            <div class="feature-highlights">
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">🔄</div>
                <div class="feature-highlight-text">Live ball updates via push, no page refresh</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">⚡</div>
                <div class="feature-highlight-text">Persistent server connections</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">🎯</div>
                <div class="feature-highlight-text">Reliable during high-traffic moments</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">✅</div>
                <div class="feature-highlight-text">Always shows current match state</div>
              </div>
            </div>
          </div>
          <div class="feature-image slide-in-right">
            <img
              :src="imgMatches"
              alt="Live Updates"
            >
          </div>
        </div>

        <!-- Feature 4: Rich Data Views -->
        <div class="feature reverse">
          <div class="feature-text slide-in-right">
            <div class="feature-label">Comprehensive Information</div>
            <h3>6 Tab-focused widget view</h3>
            <p>Users can switch between specialized tabs to find exactly what they're looking for.</p>
            <div class="feature-highlights nine-grid">
              <!-- <div class="feature-highlight-item">
                <div class="feature-highlight-icon">ℹ️</div>
                <div class="feature-highlight-text">Match Info &amp; Status</div>
              </div> -->
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">📋</div>
                <div class="feature-highlight-text">Detailed Scorecards</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">🎙️</div>
                <div class="feature-highlight-text">Ball-by-ball Commentary</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">🔢</div>
                <div class="feature-highlight-text">Over-by-over Breakdown</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">👥</div>
                <div class="feature-highlight-text">Squad Lineup and Partnerships</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">📊</div>
                <div class="feature-highlight-text">Team and Player Stats</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">📈</div>
                <div class="feature-highlight-text">Performance Graphs</div>
              </div>
              <!-- <div class="feature-highlight-item">
                <div class="feature-highlight-icon">🎮</div>
                <div class="feature-highlight-text">Fantasy Points</div>
              </div>
              <div class="feature-highlight-item">
                <div class="feature-highlight-icon">💰</div>
                <div class="feature-highlight-text">Betting Odds</div>
              </div> -->
            </div>
          </div>
          <div class="feature-image slide-in-left">
            <img :src="imgScorecard" alt="Data Views">
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 5: PRICING -->
    <section class="pricing">
      <div class="pricing-container">
        <h2 class="fade-in">Simple, Transparent Pricing</h2>
        <p class="pricing-subtitle fade-in">One price, both widgets, everything included.
          No hidden fees or usage limits.</p>
        <div class="pricing-card fade-in">
          <template v-if="widgetPlan">
            <div class="price">
              {{ formatCurrency(widgetPlan.price.unit_amount[currency], currency) }}
            </div>
            <div class="period">
              {{ widgetPlan.price.unit_label || 'Per month' }}
            </div>
          </template>
          <template v-else>
            <div class="price">—</div>
            <div class="period">Per month</div>
          </template>
          <div class="tagline">Everything included. No hidden fees, no surprise charges.</div>
          <ul class="pricing-features">
            <li>Both Matches and Tournament widgets</li>
            <li>Unlimited matches and tournaments</li>
            <li>Real-time updates</li>
            <!-- <li>All nine information tabs</li> -->
            <li>Mobile responsive design</li>
            <!-- <li>Dark mode built-in</li> -->
            <li>Configure up to 5 domains</li>
            <!-- <li>Production-ready, no maintenance</li> -->
          </ul>
          <TextButton
            :buttonType="'rs-button cta'"
            :link="{
              name: 'Get started',
              url: widgetPlan
                ? '/v5/user/project/review/?package=' + widgetPlan.key + '&step=ReviewPlan'
                : '/v5/user/project/review'
            }"
          />
        </div>
      </div>
    </section>

    <!-- SECTION 6: GET STARTED -->
    <section class="get-started">
      <div class="container">
        <h2 class="fade-in">Getting Started</h2>
        <div class="steps">
          <div class="step">
            <div class="step-number">01</div>
            <h3>Purchase the widget package</h3>
            <p>Buy Cricket Widget and get immediate access to your dashboard where you can
              generate keys and configure your widgets.</p>
          </div>
          <div class="step">
            <div class="step-number">02</div>
            <h3>Generate and configure your keys</h3>
            <p>Create your Public Key in the dashboard and add your website domains (up to 5).
              Share the keys with your development team.</p>
          </div>
          <div class="step">
            <div class="step-number">03</div>
            <h3>Your developers integrate the widgets</h3>
            <p>Your team follows our integration guide to install the widget SDK and embed the components
              on your site. Works with any framework: HTML, React, Vue, Angular, or Next.js.</p>
          </div>
        </div>
        <!-- <div class="get-started-cta fade-in">
          <TextButton
            :buttonType="'rs-button yellow-cta'"
            :link="{ name: 'Start now', url: '/v5/user/project/review' }"
          />
        </div>
        <div class="questions">
          Questions?
          <TextButton
            :buttonType="'rs-button plain-cta'"
            :link="{ name: 'Talk to our team', url: 'https://cal.com/ashiey-joe-pfn0im', target: '_blank' }"
          />
        </div> -->
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import TextButton from '@/components/RZWebsite/TextButton.vue';
import { Package } from '@/store/modules/paygo';
import { currencyMethodsMixin } from '@/mixin/currency';

const BASE = 'https://assets.sports.dev.roanuz.com/uploads-v5/cricket/website/banners/23mar2026';
const BASE2 = 'https://assets.sports.dev.roanuz.com/uploads-v5/cricket/website/banners/10april2026';

export default {
  name: 'WidgetResourcePage',
  components: {
    TextButton,
  },
  mixins: [currencyMethodsMixin],
  computed: {
    ...mapState({
      currency: (state) => state.user.activeCurrency,
    }),
  },
  data() {
    return {
      widgetPlan: null,
      imgScheduled: `${BASE}/Scheduled-desktop.png`,
      imgMatches: 'https://assets.sports.dev.roanuz.com/uploads-v5/cricket/website/banners/23mar2026/matches.png',
      imgScorecard: `${BASE}/scorecard-widget.png`,
      imgTournament: `${BASE2}/Container-11.png`,
    };
  },
  serverPrefetch() {
    return this.fetchData();
  },
  mounted() {
    this.fetchData();
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const stepsObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll('.step').forEach((step) => {
            step.classList.add('visible');
          });
        }
      });
    }, { threshold: 0.3, rootMargin: '0px 0px -100px 0px' });

    this.$el.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right').forEach((el) => observer.observe(el));

    const stepsContainer = this.$el.querySelector('.steps');
    if (stepsContainer) stepsObserver.observe(stepsContainer);
  },
  methods: {
    fetchData() {
      return this.$store.dispatch('link/fetchRsFooter').then(() => this.fetchPackagePricing());
    },
    fetchPackagePricing() {
      return Package.getPackagePricing(this.$apiInstance, this.$inMemoryCache).then((resp) => {
        const plans = resp.data.data.response.filter((p) => p.active && p.show_in_website);
        this.widgetPlan = plans.find((p) => p.name === 'Cricket Widget') || null;
      }).catch((err) => {
        console.log('Error fetching widget pricing', err.message);
      });
    },
  },
};
</script>

<style scoped>
.widget-resource-page {
  font-family: var(--rz-hero-font);
  line-height: 1.6;
  color: var(--rs-black-color);
  background: var(--rs-white-color);
  overflow-x: hidden;
}

.fade-in {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}
.fade-in.visible {
  opacity: 1;
  transform: translateY(0);
}
.slide-in-left {
  opacity: 0;
  transform: translateX(-50px);
  transition: opacity 0.7s ease-out, transform 0.7s ease-out;
}
.slide-in-left.visible {
  opacity: 1;
  transform: translateX(0);
}
.slide-in-right {
  opacity: 0;
  transform: translateX(50px);
  transition: opacity 0.7s ease-out, transform 0.7s ease-out;
}
.slide-in-right.visible {
  opacity: 1;
  transform: translateX(0);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Hero — matches solution-banner resource (Banner.vue) */
.hero {
  background: var(--rs-dark-blue-color);
  min-height: 37.5rem;
  clip-path: polygon(0 0, 100% 0, 100% 95%, 0 85%);
  padding: 0 1.25rem 3.125rem;
}
.hero .container {
  padding-top: 3.125rem;
}
.hero h1 {
  font-size: var(--rs-title-level0-font-size);
  line-height: var(--rs-title-level0-line-height);
  font-weight: 800;
  color: var(--rs-white-color);
  margin-bottom: 0.625rem;
  max-width: 36.25rem;
}
.hero p {
  font-size: var(--rs-solution-para-font-size);
  line-height: var(--rs-solution-para-line-height);
  color: var(--rs-white-color);
  margin-bottom: 1.25rem;
  max-width: 18.75rem;
}
.hero-cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
@media (min-width: 48rem) {
  .hero {
    min-height: 40.625rem;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 80%);
  }
  .hero .container {
    padding-top: 4.6875rem;
  }
  .hero p {
    max-width: 37.5rem;
  }
}
@media (min-width: 74.625rem) {
  .hero {
    min-height: 37.5rem;
    padding: 0 0 3.125rem;
  }
  .hero .container {
    padding-top: 6.25rem;
    max-width: 67.5rem;
    padding-left: 0;
    padding-right: 0;
  }
  .hero p {
    max-width: 33.75rem;
  }
}

.pricing-card ::v-deep .text-button.rs-button {
  display: block;
  width: 100%;
}
.pricing-card ::v-deep .text-button.rs-button .link {
  display: block;
  width: 100%;
  text-align: center;
  background: var(--hero-text);
  color: var(--rs-white-color);
  border: 1px solid var(--hero-text);
  padding: 12px 0;
  border-radius: 5px;
  margin-top: 16px;
  font-weight: 700;
  cursor: pointer;
}
.pricing-card ::v-deep .text-button.rs-button .link::after {
  border-color: var(--rs-white-color);
}

/* Match Widget.vue button horizontal padding */
::v-deep .rs-button .link {
  padding-right: 1rem;
  padding-left: 1rem;
}
@media (min-width: 48rem) {
  ::v-deep .rs-button .link {
    padding-right: 1.5rem;
    padding-left: 1.5rem;
  }
}

/* What is */
.what-is {
  padding: 100px 0;
  background: var(--rs-white-color);
}
.what-is h2,
.transition h2,
.pricing h2,
.get-started h2 {
  padding-right: 0;
}

.what-is h2 {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 16px;
  text-align: center;
}
.what-is-content {
  max-width: 900px;
  margin: 0 auto;
}
.what-is-intro {
  font-size: var(--rs-title-level4-font-size);
  line-height: var(--rs-title-level4-line-height);
  color: var(--rs-black-color);
  margin-bottom: 48px;
  text-align: center;
  font-weight: 500;
}
.widget-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-bottom: 48px;
}
.widget-card {
  background: var(--rs-white-color);
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.widget-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}
.widget-card h3 {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--rs-black-color);
}
.widget-card p {
  font-size: 16px;
  color: var(--rs-black-color);
  line-height: 1.7;
}
.what-is-footer {
  font-size: 18px;
  line-height: 1.8;
  color: var(--rs-black-color);
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

/* Transition */
.transition {
  padding: 80px 0;
  text-align: center;
  background: var(--rs-white-color);
}
.transition h2 {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 16px;
  text-align: center;
}
.transition p {
  font-size: 20px;
  color: var(--rs-black-color);
  margin: 0 auto 60px;
  text-align: center;
}

/* Features */
.features {
  background: var(--rs-white-color);
  padding-bottom: 100px;
}
.feature {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  margin-bottom: 120px;
}
.feature:last-child {
  margin-bottom: 0;
}
.feature-text h3 {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 20px;
  color: var(--rs-black-color);
  line-height: 1.3;
}
.feature-label {
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: var(--rz-link-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}
.feature-text p {
  font-size: var(--rs-solution-para-font-size);
  line-height: var(--rs-solution-para-line-height);
  color: var(--rs-black-color);
  margin-bottom: 20px;
}
.feature-highlights {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 24px;
}
.feature-highlights.nine-grid {
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.feature-highlight-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: var(--rs-white-color);
  border-radius: 8px;
  transition: background 0.3s ease;
}
.nine-grid .feature-highlight-item {
  padding: 14px 12px;
  align-items: center;
}
.feature-highlight-item:hover {
  background: var(--rs-white-color);
}
.feature-highlight-icon {
  font-size: 20px;
  flex-shrink: 0;
}
.nine-grid .feature-highlight-icon {
  font-size: 18px;
}
.feature-highlight-text {
  font-size: 15px;
  color: var(--rs-black-color);
  line-height: 1.5;
}
.nine-grid .feature-highlight-text {
  font-size: 14px;
  line-height: 1.4;
}
.feature-image {
  border-radius: 16px;
  overflow: hidden;
}
.feature-image img {
  width: 100%;
  height: auto;
  display: block;
}
.feature.reverse {
  direction: rtl;
}
.feature.reverse > * {
  direction: ltr;
}

/* Pricing */
.pricing {
  background: var(--rs-white-color);
  padding: 100px 0;
}
.pricing-container {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.pricing h2 {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 24px;
  text-align: center;
}
.pricing-subtitle {
  font-size: 18px;
  color: var(--rs-black-color);
  margin: 0 auto 48px;
  max-width: 600px;
  text-align: center;
}
.pricing-card {
  background: var(--rs-white-color);
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  text-align: left;
  width: 100%;
  max-width: 500px;
}
.pricing-card .price {
  font-size: 56px;
  font-weight: 800;
  color: var(--rs-black-color);
  margin-bottom: 8px;
}
.pricing-card .period {
  font-size: 18px;
  color: var(--rs-black-color);
  margin-bottom: 32px;
}
.pricing-card .tagline {
  font-size: 16px;
  color: var(--rs-black-color);
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid #e5e7eb;
}
.pricing-features {
  list-style: none;
  margin-bottom: 32px;
  padding: 0;
}
.pricing-features li {
  font-size: 16px;
  color: var(--rs-black-color);
  margin-bottom: 16px;
  padding-left: 32px;
  position: relative;
}
.pricing-features li::before {
  content: "✓";
  position: absolute;
  left: 0;
  color: var(--rz-link-color);
  font-weight: bold;
  font-size: 20px;
}

/* Get Started */
.get-started {
  background: var(--rs-white-color);
  padding: 100px 0;
}
.get-started h2 {
  font-size: 42px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 80px;
}
.steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 60px;
  max-width: 1000px;
  margin: 0 auto;
}
.step {
  text-align: center;
}
.step-number {
  font-size: var(--rs-title-level0-font-size);
  line-height: var(--rs-title-level0-line-height);
  font-weight: bold;
  color: var(--rs-black-color);
  margin-bottom: 16px;
}
.step h3 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--rs-black-color);
}
.step p {
  font-size: 18px;
  line-height: 1.8;
  color: var(--rs-black-color);
  margin-bottom: 20px;
}
.get-started-cta {
  text-align: center;
  margin-top: 60px;
}
.get-started-cta .btn-primary {
  font-size: 18px;
  padding: 18px 40px;
}
.questions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 32px;
  font-size: 16px;
  color: var(--rs-black-color);
}
.questions ::v-deep .text-button .link {
  margin-top: 0;
  padding: 0;
  border: none;
  background: transparent;
}

/* Responsive */
@media (max-width: 768px) {
  .hero h1 { font-size: 36px; }
  .hero p { font-size: 18px; }
  .widget-cards { grid-template-columns: 1fr; }
  .feature {
    grid-template-columns: 1fr;
    gap: 40px;
    margin-bottom: 80px;
  }
  .feature.reverse { direction: ltr; }
  .feature-highlights { grid-template-columns: 1fr; }
  .feature-highlights.nine-grid { grid-template-columns: 1fr 1fr; }
  .steps { grid-template-columns: 1fr; gap: 60px; }
  .what-is h2,
  .transition h2,
  .pricing h2,
  .get-started h2 { font-size: 32px; }
  .feature-text h3 { font-size: 24px; }
  .pricing-card { min-width: auto; width: 100%; }
}
</style>
